/**
 * Section Badge Classification
 *
 * Post-focus classifier: annotates badges whose advice is misleading even
 * though their number is honest. Within a section the focus window's
 * countermovement guard already nets trades, so compensation lives ACROSS
 * the section's end cut — hotspot-atomic sections and rule-b braking cuts
 * can slice a trade so the regain lands in the next section, where the
 * guard cannot see it. The classifier reads the drift curves past the cut.
 *
 * Open-closed over the badge pipeline: consumes FocusedSectionBadge and
 * TrackZone[] without modifying either; never suppresses, re-values, or
 * re-orders a badge — every failure mode passes the badge through
 * unclassified (annotation is best-effort).
 * Spec: docs/superpowers/specs/2026-07-30-section-badge-classification-design.md
 */

import { type TrackZone } from '../../analysis/zones/TrackZoneTypes'
import { type TimeDriftResult } from '../../domain/lap/TimeDriftCalculator'
import {
  createSectionCurveSampler,
  type FocusedSectionBadge,
  type TimeSection,
} from './sectionTimeDeltas'
import { maxOf, minOf } from '../../utils/extremes'

export interface SectionBadgeClassification {
  kind: 'compensated' | 'selfContained'
  /**
   * Net from the focused window's start through the lookahead end: the
   * median of the agreeing laps' per-lap deltas — the same construction as
   * the displayed badge value, so "shown +198, net +48" reads coherently.
   * Signed like deltaMs (positive = time lost vs reference).
   */
  netMs: number
  /** Where net stops counting, meters from lap start (mod lap length) —
   * for a future chart surface and debugging. */
  lookaheadEndDistance: number
}

/** A focused badge, optionally annotated. Order and values untouched. */
export interface ClassifiedSectionBadge extends FocusedSectionBadge {
  classification?: SectionBadgeClassification
}

export interface SectionClassificationOptions {
  /** Lookahead never extends further than this past the section end cut. */
  lookaheadCapMeters: number
  /** Compensated needs at least this fraction of the badge regained... */
  regainFraction: number
  /** ...and at least this many ms — the give-back must itself be
   * badge-worthy (mirrors DEFAULT_SECTION_BADGE_OPTIONS.minDeltaMs). */
  regainFloorMs: number
  /** Self-contained tolerates at most this fraction of further movement
   * in the badge's direction... */
  flatFraction: number
  /** ...or this many ms, whichever is larger. */
  flatFloorMs: number
  /** Sampling step handed to the section curve sampler. */
  sampleStepMeters: number
}

export const DEFAULT_SECTION_CLASSIFICATION_OPTIONS: SectionClassificationOptions =
  {
    lookaheadCapMeters: 300,
    regainFraction: 0.5,
    regainFloorMs: 100,
    flatFraction: 0.15,
    flatFloorMs: 30,
    sampleStepMeters: 10,
  }

/** A rule-b section already ends AT a braking entry; within this tolerance
 * the lookahead is zero-length and the badge stays unclassified. */
const CUT_TOLERANCE_METERS = 1

/**
 * Lookahead LENGTH: meters past the section's end cut to the next braking
 * zone's start (circular search in the zone frame), truncated at the cap.
 * Null when the cut already sits at a braking entry or the zone frame is
 * unusable — "this corner's story" ends where the next one begins.
 */
export function resolveLookaheadLength(
  sectionEndDistance: number,
  zones: TrackZone[],
  options: Pick<SectionClassificationOptions, 'lookaheadCapMeters'>
): number | null {
  const trackLength = Math.max(0, maxOf(zones.map((z) => z.endDistance)))
  if (trackLength <= 0) return null
  let nearest = Infinity
  for (const z of zones) {
    if (z.type !== 'braking') continue
    const forward =
      (z.startDistance - sectionEndDistance + trackLength) % trackLength
    if (forward < nearest) nearest = forward
  }
  if (nearest <= CUT_TOLERANCE_METERS) return null
  return Math.min(nearest, options.lookaheadCapMeters)
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Any acceleration zone overlapping the lookahead (circular zone frame).
 * Gates the selfContained tag: rule-a cuts sit mid-straight BECAUSE the
 * drift is flat there and those sections already contain their exit — a
 * flat lookahead behind them is trivially expected and would tag nearly
 * every healthy badge. The tag only means something when the accel phase
 * lies ahead of the cut (hotspot-atomic sections that ended mid-event). */
function accelZoneInLookahead(
  sectionEndDistance: number,
  lookaheadLength: number,
  zones: TrackZone[]
): boolean {
  const trackLength = Math.max(0, maxOf(zones.map((z) => z.endDistance)))
  if (trackLength <= 0) return false
  for (const z of zones) {
    if (z.type !== 'acceleration') continue
    const startForward =
      (z.startDistance - sectionEndDistance + trackLength) % trackLength
    const zoneLength = z.endDistance - z.startDistance
    // Overlap with [0, lookaheadLength): the zone starts inside the window,
    // or straddles the cut (its wrapped tail covers the window start).
    if (
      startForward < lookaheadLength ||
      startForward + zoneLength > trackLength
    ) {
      return true
    }
  }
  return false
}

/**
 * Annotate focused badges whose advice is misleading. Two tags:
 *
 * - `compensated`: at least max(regainFraction·|badge|, regainFloorMs) of
 *   the shown number comes back between the focused window's start and the
 *   lookahead end — the number is honest but "fix this corner" is not; the
 *   net is what the corner really costs through its exit.
 * - `selfContained`: the lookahead adds at most max(flatFraction·|badge|,
 *   flatFloorMs) in the badge's direction AND an acceleration zone lies in
 *   the lookahead — the loss stops at the cut instead of compounding
 *   through the exit (net ≈ badge; the tag corrects the implied advice).
 *
 * Net is the median of the agreeing laps' per-lap deltas from the focused
 * window's start to the lookahead end — the same population and
 * construction as the displayed value, endpoint-based on purpose (a
 * divot-then-re-loss lookahead honestly nets out). Everything else —
 * value, order, ranking, colors — passes through untouched, and every
 * failure mode (zero-length lookahead, missing section/curves, unusable
 * sampler) returns the badge as-is.
 */
export function classifySectionBadges(
  badges: FocusedSectionBadge[],
  sections: TimeSection[],
  zones: TrackZone[],
  drifts: ReadonlyMap<string, TimeDriftResult>,
  options: SectionClassificationOptions = DEFAULT_SECTION_CLASSIFICATION_OPTIONS
): ClassifiedSectionBadge[] {
  const sectionsById = new Map(sections.map((s) => [s.id, s]))
  // Same shared frame as the badge pipeline: the wrap seam is the shortest
  // curve's final distance (identical to focusSectionBadges).
  const allCurves = [...drifts.values()].filter((d) => d.points.length >= 2)
  const lapEnd =
    allCurves.length > 0
      ? minOf(
          allCurves.map((d) => d.points[d.points.length - 1].distanceMeters)
        )
      : 0

  return badges.map((b): ClassifiedSectionBadge => {
    if (lapEnd <= 0) return b
    const s = sectionsById.get(b.sectionId)
    if (!s) return b
    const lookahead = resolveLookaheadLength(s.endDistance, zones, options)
    if (lookahead == null) return b
    const agreeingCurves = b.agreeingLapIds
      .map((id) => drifts.get(id))
      .filter((c): c is TimeDriftResult => c != null && c.points.length >= 2)
    if (agreeingCurves.length === 0) return b

    // Unwrapped frame from the focused window's start: forward to the end
    // cut (both mod-lap in the shared drift frame), then the lookahead.
    // Crossing the seam flags the synthetic section as a wrap so the
    // sampler's seam-shift keeps every curve continuous.
    const coreStart = b.coreStartDistance
    const toSectionEnd = (s.endDistance - coreStart + lapEnd) % lapEnd
    const unwrappedEnd = coreStart + toSectionEnd + lookahead
    const wraps = unwrappedEnd > lapEnd
    const synthetic: TimeSection = {
      id: `${b.sectionId}-classify`,
      startDistance: coreStart,
      endDistance: wraps ? unwrappedEnd - lapEnd : unwrappedEnd,
      zoneIds: [],
      wraps,
    }
    const sampler = createSectionCurveSampler(
      synthetic,
      agreeingCurves,
      lapEnd,
      options.sampleStepMeters
    )
    if (!sampler) return b

    const netMs = median(
      agreeingCurves.map(
        (c) => sampler.valueAt(c, unwrappedEnd) - sampler.valueAt(c, coreStart)
      )
    )
    if (!Number.isFinite(netMs)) return b

    // Orient so the badge's direction is positive: the same trade math
    // covers loss badges and gain badges symmetrically.
    const sign = b.deltaMs >= 0 ? 1 : -1
    const magnitude = Math.abs(b.deltaMs)
    const oriented = sign * netMs
    const lookaheadEndDistance = wraps ? unwrappedEnd - lapEnd : unwrappedEnd

    if (
      magnitude - oriented >=
      Math.max(options.regainFraction * magnitude, options.regainFloorMs)
    ) {
      return {
        ...b,
        classification: { kind: 'compensated', netMs, lookaheadEndDistance },
      }
    }
    if (
      oriented - magnitude <=
        Math.max(options.flatFraction * magnitude, options.flatFloorMs) &&
      accelZoneInLookahead(s.endDistance, lookahead, zones)
    ) {
      return {
        ...b,
        classification: { kind: 'selfContained', netMs, lookaheadEndDistance },
      }
    }
    return b
  })
}
